import React, {createContext, useContext, useEffect, useState} from "react"

type Theme = "light" | "dark"

interface ThemeContextValue {
    theme: Theme
    setTheme: (t: Theme) => void
    toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function currentHtmlTheme(): Theme {
    if (typeof document === "undefined") return "light"
    return document.documentElement.classList.contains("dark") ? "dark" : "light"
}

function applyTheme(t: Theme) {
    const root = document.documentElement
    root.classList.toggle("dark", t === "dark")
    root.style.colorScheme = t
}

export function ThemeProvider({children}: { children: React.ReactNode }) {
    // Initialize from what the inline <head> script already applied — no flash, no mismatch.
    const [theme, setThemeState] = useState<Theme>(currentHtmlTheme)

    const setTheme = (t: Theme) => {
        localStorage.setItem("theme", t)
        applyTheme(t)
        setThemeState(t)
    }

    // Follow the OS preference only while the user hasn't made an explicit choice.
    useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)")
        const onChange = () => {
            if (localStorage.getItem("theme")) return
            const t: Theme = mq.matches ? "dark" : "light"
            applyTheme(t)
            setThemeState(t)
        }
        mq.addEventListener("change", onChange)
        return () => mq.removeEventListener("change", onChange)
    }, [])

    const toggle = () => setTheme(theme === "dark" ? "light" : "dark")

    return (
        <ThemeContext.Provider value={{theme, setTheme, toggle}}>
            {children}
        </ThemeContext.Provider>
    )
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext)
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider")
    return ctx
}
